/**
 * Spectator badge (C-T22 §2). Anyone at a table without a seat once play has started: someone who
 * arrived mid-match, or a player who stood up.
 *
 * A spectator can watch and can emote — the server relays those — but has no bid or play controls,
 * because there is no hand to act with. The one action offered is taking a seat at the next hand
 * boundary.
 *
 * ## Why "Seated next hand" is optimistic
 *
 * `sit` mid-hand is recorded by the server (`session.pendingSeat`) and then answered with an
 * *error*: `bad_phase`, "table is mid-hand; you will be seated at the next hand". Nothing in the
 * table view exposes the pending seat, so the client cannot read the state back. This tracks it
 * locally instead: the request is treated as accepted unless the error says otherwise, and the
 * local flag clears the moment a real seat arrives. A genuine refusal (a full table) still toasts.
 */
import { useEffect, useState } from 'react';
import { useStore } from '../../store/store';
import { isQueuedSitError, isSpectating } from '../lobby';
import { spectatorReason } from '../states';
import { getRuntime, intents } from '../runtime';
import { Button, toast } from '../ui';

export function Spectator() {
  const seat = useStore((s) => s.session.seat);
  const phase = useStore((s) => s.table?.phase);
  const spectators = useStore((s) => s.table?.spectators ?? 0);
  const seatedHumans = useStore((s) => s.table?.seats.filter((x) => x.controller === 'human').length ?? 0);
  const [pending, setPending] = useState(false);

  // Never during the tutorial. The scripted table is mid-hand by design, so the spectator rule
  // would fire on any beat before the player is seated — and "Watching / Sit next hand" over a
  // guided first hand is both wrong and the most confusing thing on the screen.
  const screen = useStore((s) => s.presentation.screen);
  const watching = screen !== 'tutorial' && isSpectating({ seat, phase });

  // A real seat supersedes the optimistic flag; so does leaving the spectator state entirely.
  useEffect(() => {
    if (seat !== null || !watching) setPending(false);
  }, [seat, watching]);

  // A queued sit arrives as an error, so the error channel is the only confirmation available.
  // Anything else is a genuine refusal and must not leave the button claiming success.
  useEffect(() => {
    if (!pending) return;
    const off = getRuntime()?.driver.onError?.((code, msg) => {
      if (isQueuedSitError(code, msg)) return;
      setPending(false);
      toast(msg || 'Could not take a seat', 'error', 3000);
    });
    return off;
  }, [pending]);

  if (!watching) return null;

  const reason = spectatorReason({ seatedHumans, phase });

  const others = Math.max(0, spectators - 1);

  return (
    <div className="spectator" data-testid="spectator">
      <span className="spectator__badge" title={reason}>
        <span className="spectator__dot" aria-hidden />
        Watching
        {others > 0 ? ` · ${others} more` : ''}
      </span>
      {/* Why, not just what. A full table is the common case and the one most likely to read as a
          bug — four humans are already sitting and nothing is broken. */}
      <span className="spectator__why small">{reason}</span>
      {pending ? (
        <span className="spectator__pending" data-testid="seated-next-hand" role="status">
          Seated next hand
        </span>
      ) : (
        <Button
          testId="sit-next-hand"
          onClick={() => {
            setPending(true);
            intents()?.sit();
          }}
        >
          Sit next hand
        </Button>
      )}
    </div>
  );
}
